import React, { HTMLAttributes } from "react";
import ReactDOM from "react-dom";
import debounce from "lodash.debounce";
import { inject, observer } from "mobx-react";
import _ from "lodash";
import cx from "classnames";
import math from "core/math";
import { Vector3 } from "three/src/math/Vector3";
import Helper from "helper/index";
import Config from "config";
import ElementViewConfigStore from "pages/viewer-dashboard/store/ElementViewConfigStore";
import "./OrientationMarkers.styl";
/**
 * @function getOrientationString
 * @description DICOM 태그에서 얻은 방향 벡터를 이용해 뷰어에 표시할 방향 문자를 획득
 * @param vector DICOM 태그에서 얻은 방향 벡터
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 방향 문자
 */
function getOrientationString(vector: any) {
  const vec3 = math.vector.convertToVector3(vector);

  let orientation = "";
  const orientationX = vec3.x < 0 ? "R" : "L";
  const orientationY = vec3.y < 0 ? "A" : "P";
  const orientationZ = vec3.z < 0 ? "F" : "H";

  // Should probably make this a function vector3.abs
  const abs = new Vector3(Math.abs(vec3.x), Math.abs(vec3.y), Math.abs(vec3.z));

  for (let i = 0; i < 3; i++) {
    if (abs.x > 0.0001 && abs.x > abs.y && abs.x > abs.z) {
      orientation += orientationX;
      abs.x = 0;
    } else if (abs.y > 0.0001 && abs.y > abs.x && abs.y > abs.z) {
      orientation += orientationY;
      abs.y = 0;
    } else if (abs.z > 0.0001 && abs.z > abs.x && abs.z > abs.y) {
      orientation += orientationZ;
      abs.z = 0;
    } else {
      break;
    }
  }

  return orientation;
}

/**
 * @function invertOrientationString
 * @description DICOM 영상에서 표시되는 방향 문자를 입력받아 반대 방향 문자로 변경하여 리턴
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 변환된 방향 문자
 */
function invertOrientationString(string: any) {
  let inverted = string.replace("H", "f");

  inverted = inverted.replace("F", "h");
  inverted = inverted.replace("R", "l");
  inverted = inverted.replace("L", "r");
  inverted = inverted.replace("A", "p");
  inverted = inverted.replace("P", "a");
  inverted = inverted.toUpperCase();

  return inverted;
}

/**
 * Computes the orientation labels on a Viewport element
 * when the viewport settings change (e.g. when a horizontal flip or a rotation occurs)
 *
 * @param imageId The image data
 * @param viewport The current viewport
 */

/**
 * @function getOrientationString
 * @description 뷰포트 설정에 따른 방향 문자 획득(좌/우 위/아래 Flip 반영)
 * @param imageId 이미지 데이터
 * @param viewport 뷰포트 정보
 * @see {@link }
 * @todo link에 ohif 또는 관련 참고 문헌 추가필요
 * @return 방향 문자
 */
export function getOrientationMarkers(
  rowCosines: any,
  columnCosines: any,
  hflip: any,
  vflip: any,
  rotation: any
) {
  if (!rowCosines || !columnCosines) {
    return;
  }

  const rowString = getOrientationString(rowCosines);
  const columnString = getOrientationString(columnCosines);
  const oppositeRowString = invertOrientationString(rowString);
  const oppositeColumnString = invertOrientationString(columnString);

  const markers = {
    top: oppositeColumnString,
    left: oppositeRowString,
    bottom: columnString,
    right: rowString,
  };

  // If any vertical or horizontal flips are applied, change the orientation strings ahead of
  // the rotation applications
  if (vflip) {
    markers.bottom = markers.top;
    markers.top = invertOrientationString(markers.top);
  }

  if (hflip) {
    markers.right = markers.left;
    markers.left = invertOrientationString(markers.left);
  }

  // Swap the labels accordingly if the viewport has been rotated
  // This could be done in a more complex way for intermediate rotation values (e.g. 45 degrees)
  if (rotation === 90 || rotation === -270) {
    return {
      top: markers.left,
      left: invertOrientationString(markers.top),
      bottom: invertOrientationString(markers.left),
      right: markers.top,
    };
  } else if (rotation === -90 || rotation === 270) {
    return {
      top: invertOrientationString(markers.left),
      left: markers.top,
      bottom: markers.left,
      right: invertOrientationString(markers.top),
    };
  } else if (rotation === 180 || rotation === -180) {
    return {
      top: invertOrientationString(markers.top),
      left: invertOrientationString(markers.left),
      bottom: markers.top,
      right: markers.right,
    };
  }

  return markers;
}

/**
 * @description 프로퍼티 타입 정의
 *
 * @interface IOrientationMarkerProps
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IOrientationMarkerProps extends HTMLAttributes<HTMLDivElement> {
  rowCosines: any;
  columnCosines: any;
  hflip: any;
  vflip: any;
  rotation: any;
  styles: any;
  frameRange: any;
  width: number;
  height: number;
  isFocus: boolean;
  elementViewConfigStore: ElementViewConfigStore;
}

/**
 * @function ViewportOrientationMarkers
 * @description DICOM 영상에 방향 문자를 오버레이하는 UI 컴포넌트
 * @param {*} { imageId = null, viewport = null }
 * @returns 렌더링된 React Element
 */
@inject("elementViewConfigStore")
@observer
class OrientationMarker extends React.PureComponent<
  IOrientationMarkerProps,
  any
> {
  element: any = null;

  public static defaultProps = {
    styles: {
      fontSize: "15px",
      transform: `scale(1)`,
      lineHeight: "11px",
    },
  };

  constructor(props: any) {
    super(props);
    this.state = {
      style: props.style,
      prevWidthRatio: 0,
      prevHeightRatio: 0,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleWindowResize);
    /** Max Tag Size */
    const baseFontSize = 15;
    const baseLineHeight = 26;
    if (Helper.Validator.isDefined(this.element) === false) {
      return;
    }
    const clientShortSideLength = Math.min(
      this.element.clientHeight,
      this.element.clientWidth
    );
    const ratio = Math.max(
      Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
      0.3
    );

    this.setState({
      prevWidthRatio: this.props.width,
      prevHeightRatio: this.props.height,
      style: {
        fontSize: baseFontSize + "px",
        transform: `scale(${ratio})`,
        lineHeight: baseLineHeight * ratio + "px",
      },
    });
  }
  componentWillUnmount() {
    let element = ReactDOM.findDOMNode(this);
    if (element !== null) {
      window.removeEventListener("resize", this.handleWindowResize);
    }
  }
  componentDidUpdate(prevProps: any, prevState: any) {
    if (
      !(this.props.width === this.state.prevWidthRatio) ||
      !(this.props.height === this.state.prevHeightRatio) ||
      this.props.isFocus !== prevProps.isFocus
    ) {
      /** Max Tag Size */
      const baseFontSize = 15;
      const baseLineHeight = 26;
      if (Helper.Validator.isDefined(this.element) === false) {
        return;
      }
      const clientShortSideLength = Math.min(
        this.element.clientHeight,
        this.element.clientWidth
      );
      const ratio = Math.max(
        Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
        0.3
      );

      this.setState({
        prevWidthRatio: this.props.width,
        prevHeightRatio: this.props.height,
        style: {
          fontSize: baseFontSize + "px",
          transform: `scale(${ratio})`,
          lineHeight: baseLineHeight * ratio + "px",
        },
      });
    }
  }

  handleWindowResize = (event: any) => {
    // HACK : DCMView 크기 변화 발생시 DCMTag를 표시하는 폰트 크기를 변화시키기위해
    // 아래와 같이 setState를 실행하여 리-렌더링을 유도하게 하였음
    //this.setState({}); // 리사이즈시 속도 개선을위해 주석
    this.debouncedResize();
  };
  /**
   * @description 화면 크기 변경시, viewport 크기 변환
   * @method debouncedResize
   * @memberof Viewport
   */
  debouncedResize = debounce(() => {
    /** Max Tag Size */
    const baseFontSize = 15;
    const baseLineHeight = 26;
    if (Helper.Validator.isDefined(this.element) === false) {
      return;
    }
    const clientShortSideLength = Math.min(
      this.element.clientHeight,
      this.element.clientWidth
    );
    const ratio = Math.max(
      Math.min(clientShortSideLength / 30, baseFontSize) / baseFontSize,
      0.3
    );
    this.setState({
      style: {
        fontSize: baseFontSize + "px",
        transform: `scale(${ratio})`,
        lineHeight: baseLineHeight * ratio + "px",
      },
    });
  }, 100);
  handleToggleDicomTag() {
    this.forceUpdate();
  }

  renderOrientationMarker = () => {
    const { rowCosines, columnCosines, hflip, vflip, rotation } = this.props;

    const markers = getOrientationMarkers(
      rowCosines,
      columnCosines,
      hflip,
      vflip,
      rotation
    );
    if (!markers) {
      return "";
    }

    const { frameRange } = this.props;
    const rangeLength = frameRange["max"] - frameRange["min"] + 1;

    //const styles = _.get(this.props, "styles");
    const styles = _.get(this.state, "style");
    let rightMid = cx(
      "rightMid__without-scroll",
      "orientationMarker",
      "dicomTag"
    );
    if (rangeLength > 1) {
      // 스크롤이 있는 경우
      rightMid = cx("rightMid", "orientationMarker", "dicomTag");
    }
    return (
      <React.Fragment>
        <div className="topMid orientationMarker dicomTag" style={styles}>
          {markers.top}
        </div>
        <div className="leftMid orientationMarker dicomTag" style={styles}>
          {markers.left}
        </div>
        <div className="bottomMid orientationMarker dicomTag" style={styles}>
          {markers.bottom}
        </div>
        <div className={rightMid} style={styles}>
          {markers.right}
        </div>
        {/* <div className="rightMid orientationMarker dicomTag" style={styles}>
          {markers.right}
        </div> */}
      </React.Fragment>
    );
  };

  render() {
    // 1. Property 획득
    const { elementViewConfigStore } = this.props;

    // 2. Metadata text color 속성 정보 획득
    let metadataTextColor = elementViewConfigStore.getConfigAttribute(
      "metadataTextColor"
    );
    // 만약, 획득한 color 가 유효한 hex color 가 아닌 경우, 기본값으로 지정
    if (Helper.Validator.isHexColor(metadataTextColor) === false) {
      metadataTextColor = Config.elementView.metadataTextColor;
    }
    return (
      <div
        ref={(o) => {
          this.element = o;
        }}
        style={{ color: metadataTextColor }}
        className="viewportOrientationMarkers noselect nodrag"
      >
        {this.renderOrientationMarker()}
      </div>
    );
  }
}

export default OrientationMarker;
